import type { AiCategoryDefinition } from "@/store/slices/categories/categoriesSlice";
import type { Brand } from "@/store/slices/brands/brandsSlice";

export function brandBelongsToCategory(
  brand: Brand | undefined,
  category: AiCategoryDefinition | undefined,
): boolean {
  if (!brand || !category) return false;
  const ids = new Set(category.brandIds.map(String));
  return ids.has(String(brand.id));
}

export function filterBrandIdsByCategory(
  brandIds: (string | number)[],
  entities: Record<string | number, Brand | undefined>,
  category: AiCategoryDefinition | undefined,
): (string | number)[] {
  if (!category) return brandIds;
  return brandIds.filter((id) => brandBelongsToCategory(entities[id], category));
}

/**
 * Categories have no per-market scoping today (`AiCategoryDefinition` carries
 * no market field), so this is a plain active-status filter — not a
 * market-aware lookup.
 */
export function resolveActiveCategories(
  categories: AiCategoryDefinition[],
): AiCategoryDefinition[] {
  return categories.filter((category) => category.status === "ACTIVE");
}

/**
 * The category id the selection should hold: the requested one if it is still
 * active, else the configured default (if active), else the first active one.
 * Brand pruning and the committed `category` must both use this so the brand
 * pool, payload and dropdown agree.
 */
export function resolveSelectedCategoryId(
  activeCategories: AiCategoryDefinition[],
  requested: number | null | undefined,
  defaultCategory?: number | null,
): number | null {
  const validIds = activeCategories.map((category) => category.id);
  if (requested != null && validIds.includes(requested)) return requested;
  if (defaultCategory != null && validIds.includes(defaultCategory)) {
    return defaultCategory;
  }
  return validIds[0] ?? null;
}
